import { Component, inject, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ButtonThemes, ButtonSizes, ButtonComponent } from '../button/button.component';

import { environment } from '@environments/environment';
import { CustomerAddressPayload } from '@shared/models/customer-address/customer-address.model';
import { CustomerAddressService } from '@shared/services/customer-address/customer-address.service';
import { SelectComponent } from '../select/select.component';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CustomerStore } from '@shared/stores/customer/customer.store';
import { UserStore } from '@shared/stores/user/user.store';
import { Customer } from '@shared/models/customer/customer.model';
import { User } from '@shared/models/user/user.model';
import { CustomerService } from '@shared/services/customer/customer.service';
import { ToastService } from '@shared/services/toast/toast.service';
import { ERR_MESSAGES } from '@shared/constants/messages.constants';
import { TOAST_TYPE } from '@shared/constants/toast.constants';
import { OrderRequestResponseModalComponent } from '../order-request-response-modal/order-request-response-modal.component';
import { DigitOnlyDirective } from './digit-only.directive';
import { CUSTOMER_ADDRESS_SEARCH_MAX_PAGE_SIZE } from '@shared/utilities/common.utils';
import { DeliveryService } from '@shared/services/delivery/delivery.service';
import { mapActiveProductOptions } from '@shared/utilities/product-options.utils';
import { CustomerPortalProductOption } from '@shared/models/product/product-filter.model';

@Component({
    selector: 'avenir-order-request-modal',
    imports: [
    ButtonComponent,
    SelectComponent,
    ReactiveFormsModule,
    DigitOnlyDirective
],
    templateUrl: './order-request-modal.component.html',
    styleUrl: './order-request-modal.component.scss'
})
export class OrderRequestModalComponent implements OnInit {
  readonly BUTTON_THEME = ButtonThemes;
  readonly BUTTON_SIZE = ButtonSizes;
  @Input() customerId: number = 0;
  public modalService = inject(NgbModal);
  private modal = inject(NgbActiveModal);
  private customerAddressService = inject(CustomerAddressService);
  private deliveryService = inject(DeliveryService);

  public orderForm!: FormGroup;

  public locations: { label: string; value: number }[] = [];
  public products: { label: string; value: string }[] = [];
  locationLoading = false;
  isProductsLoading = false;
  private locationPageNumber = 1;
  private locationRecordsFiltered = 0;
  private locationSearchParam = '';
  private readonly locationPageSize = CUSTOMER_ADDRESS_SEARCH_MAX_PAGE_SIZE;
  selectedLocationLabel = '';
  selectedProductLabel = '';

  user!: User;
  customers: Customer[] = [];
  customer!: Customer;

  isTankLevelValid: boolean = true;
  isLocationValid: boolean = true;
  hasProducts = false;

  orderPropaneLabel = $localize`:@@orderPropaneLabel:Request a Delivery`
  placeholderProduct = $localize`:@@selectProduct:Select Product`
  btnCancelLabel = $localize`:@@cancel:Cancel`
  btnSubmitLabel = $localize`:@@submitOrder:Submit Order`
  tankLevelEstimateSubtext = $localize`:@@tankLevelEstimateSubtext:If you do not have a tank monitor please enter an estimate.`
  tankLevelError = $localize`:@@tankLevelError:Tank level must be between 0% and 80%.`

  constructor(
    private userStore: UserStore,
    protected customerStore: CustomerStore,
    private customerService: CustomerService,
    private toastService: ToastService,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.userStore.user$.subscribe(user => {
      this.user = this.userStore.getUser();
      this.customers = this.user.customers;
      const customer = this.customers.find(customer => customer.customerId == this.customerId);
      if (customer) {
        this.customer = customer;
      }
    })

    this.orderForm = this.fb.group({
      clientId: [environment.CLIENT_ID],
      customerId: [this.customerId],
      userId: [this.user.userId],
      customerAddressId: [0],
      productId: [null, Validators.required],
      currentTankLevel: [30, [Validators.required, Validators.min(0), Validators.max(80)]],
      deliveryInstructions: ['']
    });

    this.loadLocations(true);
  }

  loadLocations(reset = false): void {
    if (reset) {
      this.locationPageNumber = 1;
      this.locations = [];
    }

    this.locationLoading = true;
    const customerAddressPayload: CustomerAddressPayload = {
      clientId: environment.CLIENT_ID,
      mode: 'CUSTOMER',
      customerId: this.customerId,
      pageSize: this.locationPageSize,
      pageNumber: this.locationPageNumber,
      sort: 'shipToCode:asc',
      filter: {
        searchParam: this.locationSearchParam,
        siteId: '',
        siteName: '',
        streetAddress: '',
        city: '',
        state: '',
        zipCode: '',
        groupFilter: '',
        status: 'ACTIVE',
        typeFilter: 'SHIPPING',
        withTanks: true,
      },
    };

    this.customerAddressService.getCustomerAddresses(customerAddressPayload).subscribe({
      next: (res) => {
        const options = res.customerAddresses.map((address) => ({
          value: parseInt(address.customerAddressId?.toString() ?? '0', 10),
          label: `${address.shipToCode ?? ''} - ${address.street} ${address.city} ${address.state} ${address.zipCode}`,
        }));
        this.locations = reset ? options : [...this.locations, ...options];
        this.locationRecordsFiltered = res.recordsFiltered;
        this.locationPageNumber++;
        this.locationLoading = false;

        if (this.locations.length > 0 && !this.orderForm.value.customerAddressId) {
          this.isLocationValid = true;
          this.selectedLocationLabel = this.locations[0].label;
          this.orderForm.patchValue({ customerAddressId: this.locations[0].value });
          this.loadProductsForLocation(this.locations[0].value);
        } else if (this.locations.length === 0 && this.locationRecordsFiltered === 0) {
          this.isLocationValid = false;
        }
      },
      error: () => {
        this.locationLoading = false;
        this.isLocationValid = false;
      },
    });
  }

  onLocationLoadMore(): void {
    if (this.locations.length < this.locationRecordsFiltered && !this.locationLoading) {
      this.loadLocations(false);
    }
  }

  onLocationSearch(searchTerm: string): void {
    this.locationSearchParam = searchTerm;
    this.loadLocations(true);
  }

  onLocationSelected(selected: { value: number; label: string }) {
    this.selectedLocationLabel = selected.label;
    this.orderForm.patchValue({ customerAddressId: selected.value });
    this.selectedProductLabel = '';
    this.products = [];
    this.hasProducts = false;
    this.orderForm.patchValue({ productId: null });
    this.loadProductsForLocation(selected.value);
  }

  onProductSelected(selected: { value: string; label: string }) {
    this.selectedProductLabel = selected.label;
    this.orderForm.patchValue({ productId: parseInt(selected.value, 10) });
  }

  private loadProductsForLocation(customerAddressId: number): void {
    this.isProductsLoading = true;

    this.deliveryService.getProductsLists({
      clientId: environment.CLIENT_ID,
      customerId: this.customerId,
      customerAddressId,
    }).subscribe({
      next: ({ productList }) => {
        this.products = mapActiveProductOptions(productList as CustomerPortalProductOption[]);
        this.hasProducts = this.products.length > 0;
        this.isProductsLoading = false;
      },
      error: () => {
        this.products = [];
        this.hasProducts = false;
        this.isProductsLoading = false;
      },
    });
  }

  get isSubmitDisabled(): boolean {
    return !this.isTankLevelValid
      || !this.isLocationValid
      || this.isProductsLoading
      || !this.hasProducts
      || !this.orderForm.get('productId')?.value;
  }

  submitOrderRequest() {
    this.customerService.submitOrderRequest(this.orderForm.value).subscribe({
      next: (res: any) => {
        this.modal.dismiss();
        const modal = this.modalService.open(OrderRequestResponseModalComponent, { size: 'md' });
        const modalRef = modal.componentInstance as OrderRequestResponseModalComponent;
        modalRef.userName = this.user!.fullname!;
        modalRef.accountName = this.customer!.name!;
        modalRef.accountNumber = this.customer!.legacyId!;
        modalRef.location = this.locations.find((location) => location.value == this.orderForm.value['customerAddressId'])?.label || 'All';
        modalRef.product = this.selectedProductLabel;
        modalRef.currentTankLevel = this.orderForm.value['currentTankLevel'];
        modalRef.deliveryInstructions = this.orderForm.value['deliveryInstructions'];
      },
      error: (error: any) => {
        if (error.status != 500 && error.status != 401 && error.status != 403) {
          this.toastService.show(ERR_MESSAGES.GENERIC + error.error.error, TOAST_TYPE.ERROR, 3);
        }
      }
    });
  }

  onTankLevelChange(event: any) {
    let value = parseInt(event.target.value);
    if (isNaN(value) || value < 0 || value > 80) {
      if (isNaN(value) || value < 0){
        this.orderForm.patchValue({ currentTankLevel: 0 });
      }
      else if (value > 80){
        this.orderForm.patchValue({ currentTankLevel: 80 });
      }
      const tankLevelInput = document.getElementById('tankLevelInput') as HTMLInputElement;
      tankLevelInput.select();
    }
    else{
      this.isTankLevelValid = true;
    }
  }
}
